import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { paths } from "../lib/paths";
import { db } from "../lib/firebase";
import Panel from "../central/Panel";
import { HeadingMedium } from "../central/Typography";
import Button from "../central/Button";
import PageLoader from "../components/PageLoader";
import { useToast } from "../context/ToastContext";
import { useSeason } from "../context/SeasonContext";
import SeasonSelector from "../components/SeasonSelector";


const PlayerPrices = () => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { leagueID } = useParams();
  const { showToast } = useToast();

  const { selectedSeasonID } = useSeason();
  const [sortConfig, setSortConfig] = useState({key: null, direction: 'asc'});

  const sortBy = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      } else {
        return { key, direction: 'asc' };
      }
    });
  };

  useEffect(() => {
    if (!leagueID) {
      setLoading(false);
      showToast("No league ID specified.", "error");
      return;
    }
    let cancelled = false;
    const fetchPlayers = async () => {
      try {
        const playersRef = paths.leaguePlayers(leagueID, selectedSeasonID);
        const snapshot = await getDocs(playersRef);
        if (cancelled) return;
        const playersData = [];
        snapshot.forEach((doc) => {
          playersData.push({ id: doc.id, ...doc.data() });
        });
        setPlayers(playersData);
      } catch (err) {
        if (cancelled) return;
        showToast("Failed to fetch player data. Please try again.", "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPlayers();
    return () => { cancelled = true; };
  }, [leagueID, selectedSeasonID]);

  // Determine all unique gameweeks from players' priceHistory
  const allGWsSet = new Set();
  players.forEach(player => {
    if (player.priceHistory) {
      Object.keys(player.priceHistory).forEach(gw => allGWsSet.add(gw));
    }
  });
  const allGWs = Array.from(allGWsSet);
  // Sort GW keys numerically
  allGWs.sort((a, b) => {
    const aNum = parseInt(a.replace(/\D/g, ""));
    const bNum = parseInt(b.replace(/\D/g, ""));
    return aNum - bNum;
  });

  // Helper: compute latest GW price delta from priceHistory if priceDelta not present
  const getLatestDeltaFromHistory = (priceHistory) => {
    if (!priceHistory) return 0;
    const keys = Object.keys(priceHistory).sort((a, b) => {
      const aNum = parseInt(a.replace(/\D/g, ""), 10);
      const bNum = parseInt(b.replace(/\D/g, ""), 10);
      return aNum - bNum;
    });
    if (keys.length < 2) return 0;
    const last = Number(priceHistory[keys[keys.length - 1]]);
    const prev = Number(priceHistory[keys[keys.length - 2]]);
    return Number((last - prev).toFixed(1));
  };

  // Helper: render accumulator as a direct percentage, without arrow emojis
  const renderAccumulatorTrend = (accRaw) => {
    const acc = Number(accRaw ?? 0);
    const pct = acc * 1000;
    const sign = pct > 0 ? "+" : "";
    return `${sign}${Math.round(pct)}%`;
  };

  // Sort players according to sortConfig
  const sortedPlayers = React.useMemo(() => {
    if (!sortConfig.key) return players;
    const sorted = [...players];
    sorted.sort((a, b) => {
      let aVal, bVal;
      switch (sortConfig.key) {
        case "name":
          aVal = a.name?.toLowerCase() ?? "";
          bVal = b.name?.toLowerCase() ?? "";
          break;
        case "position":
          aVal = a.position?.toLowerCase() ?? "";
          bVal = b.position?.toLowerCase() ?? "";
          break;
        case "currentPrice":
          aVal = a.currentPrice ?? a.price ?? 0;
          bVal = b.currentPrice ?? b.price ?? 0;
          break;
        case "totalChange":
          aVal = Number(( (a.currentPrice ?? a.price ?? 0) - (a.originalPrice ?? 0) ).toFixed(1));
          bVal = Number(( (b.currentPrice ?? b.price ?? 0) - (b.originalPrice ?? 0) ).toFixed(1));
          break;
        case "priceDelta":
          aVal = typeof a.priceDelta === "number"
            ? a.priceDelta
            : getLatestDeltaFromHistory(a.priceHistory);
          bVal = typeof b.priceDelta === "number"
            ? b.priceDelta
            : getLatestDeltaFromHistory(b.priceHistory);
          break;
        case "priceAccumulator":
          aVal = a.priceAccumulator ?? 0;
          bVal = b.priceAccumulator ?? 0;
          break;
        default:
          aVal = "";
          bVal = "";
      }
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [players, sortConfig]);

  if (loading) return <PageLoader />;

  const getSortArrow = (key) => {
    if (sortConfig.key !== key) return "";
    return sortConfig.direction === "asc" ? " ↑" : " ↓";
  };

  return (
    <>
      <div style={{ padding: "0.75rem 1rem 0" }}>
        <SeasonSelector />
      </div>
      <Panel maxWidth="1000px">
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table className="border-collapse" style={{ minWidth: "100%", tableLayout: "auto" }}>
            <thead>
              <tr>
                <th
                  style={{
                    position: "sticky",
                    left: 0,
                    background: "white",
                    zIndex: 2,
                    cursor: "pointer",
                    maxWidth: "120px",
                    whiteSpace: "normal",
                    overflowWrap: "break-word",
                  }}
                  onClick={() => sortBy("name")}
                >
                  Player{getSortArrow("name")}
                </th>
                <th
                  style={{ cursor: "pointer", whiteSpace: "normal" }}
                  onClick={() => sortBy("position")}
                >
                  POS{getSortArrow("position")}
                </th>
                <th
                  style={{ cursor: "pointer", whiteSpace: "normal" }}
                  onClick={() => sortBy("currentPrice")}
                >
                  CUR{getSortArrow("currentPrice")}
                </th>
                <th
                  style={{ cursor: "pointer", whiteSpace: "normal" }}
                  onClick={() => sortBy("totalChange")}
                >
                  TOT{getSortArrow("totalChange")}
                </th>
                <th
                  style={{ cursor: "pointer", whiteSpace: "normal" }}
                  onClick={() => sortBy("priceDelta")}
                >
                  GW(Δ){getSortArrow("priceDelta")}
                </th>
                <th
                  style={{ cursor: "pointer", whiteSpace: "normal" }}
                  onClick={() => sortBy("priceAccumulator")}
                >
                  PPI{getSortArrow("priceAccumulator")}
                </th>
                {allGWs.map(gw => (
                  <th key={gw} style={{ whiteSpace: "normal" }}>{gw}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedPlayers.map((player, rowIdx) => {
                const rowBg = rowIdx % 2 === 0 ? "#fff" : "#f7f9fa";
                const posColor = (val) => val > 0 ? "#4CAF50" : val < 0 ? "#E53935" : "inherit";

                const totDelta = (() => {
                  const curr = Number(player.currentPrice ?? player.price ?? 0);
                  const orig = Number(player.originalPrice ?? 0);
                  return Number((curr - orig).toFixed(1));
                })();

                const gwDelta = (() => {
                  return typeof player.priceDelta === "number"
                    ? Number(player.priceDelta)
                    : getLatestDeltaFromHistory(player.priceHistory);
                })();

                const accRaw = Number(player.priceAccumulator ?? 0);
                const ppiPct = Math.round(accRaw * 1000);

                return (
                  <tr key={player.id} style={{ background: rowBg }}>
                    <td
                      style={{
                        position: "sticky",
                        left: 0,
                        background: rowBg,
                        zIndex: 1,
                        maxWidth: "120px",
                        whiteSpace: "normal",
                        overflowWrap: "break-word",
                      }}
                    >
                      {player.name}
                    </td>
                    <td style={{ whiteSpace: "normal" }}>{player.position}</td>
                    <td style={{ whiteSpace: "normal" }}>{(player.currentPrice ?? player.price)?.toFixed(1)}</td>
                    {/* TOT — color coded */}
                    <td style={{ whiteSpace: "normal", color: posColor(totDelta), fontWeight: totDelta !== 0 ? 600 : 400 }}>
                      {totDelta > 0 ? `+${totDelta.toFixed(1)}` : totDelta < 0 ? totDelta.toFixed(1) : "0.0"}
                    </td>
                    {/* GW(∆) — color coded */}
                    <td style={{ whiteSpace: "normal", color: posColor(gwDelta), fontWeight: gwDelta !== 0 ? 600 : 400 }}>
                      {gwDelta > 0 ? `+${gwDelta.toFixed(1)}` : gwDelta < 0 ? gwDelta.toFixed(1) : "0.0"}
                    </td>
                    {/* PPI — color coded */}
                    <td style={{ whiteSpace: "normal", color: posColor(ppiPct), fontWeight: ppiPct !== 0 ? 600 : 400 }}>
                      {ppiPct > 0 ? `+${ppiPct}%` : `${ppiPct}%`}
                    </td>
                    {/* Price history GW columns — color coded relative to previous GW */}
                    {allGWs.map((gw, gwIdx) => {
                      const val = player.priceHistory?.[gw];
                      if (val === undefined) return <td key={gw} style={{ whiteSpace: "normal" }}></td>;
                      const prevGW = allGWs[gwIdx - 1];
                      const prevVal = prevGW !== undefined ? player.priceHistory?.[prevGW] : undefined;
                      const diff = prevVal !== undefined ? Number(val) - Number(prevVal) : 0;
                      return (
                        <td key={gw} style={{ whiteSpace: "normal", color: posColor(diff), fontWeight: diff !== 0 ? 600 : 400 }}>
                          {Number(val).toFixed(1)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
};

export default PlayerPrices;
